import * as React from 'react'

import { Box, Button, Icon, Text } from '@factorialco/deprecated-design-system'
import { F0TagDot } from '@factorialco/f0-react'
import {
  AtsCreateQuestionTemplateDocument,
  AtsDeleteJobPostingSettingsCustomQuestionDocument,
  AtsJobPostingSettingsCustomQuestionFragment,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { CreateQuestionFormValues } from 'modules/ats/components/Questions/QuestionFormValues'
import { getQuestionTagColor, getQuestionTypeLabel } from 'modules/ats/lib/utils/customQuestion'
import { questionTypeHasOptions } from 'modules/ats/lib/utils/customQuestion'
import { validateQuestions } from 'modules/ats/lib/utils/questions_validations'

import { EditCustomQuestion } from './EditCustomQuestion'

interface Props {
  item: AtsJobPostingSettingsCustomQuestionFragment
  companyId: string
  canSaveTemplates: boolean
}

export const CustomQuestionsItem = ({ item, companyId, canSaveTemplates }: Props) => {
  const [editQuestionOpen, setEditQuestionOpen] = React.useState(false)
  const [deleteQuestionOpen, setDeleteQuestionOpen] = React.useState(false)
  const [deleteCustomQuestion, { loading: loadingDelete }] = useMutation(
    AtsDeleteJobPostingSettingsCustomQuestionDocument,
    {
      extractorFn: (data) => data.atsMutations.deleteAtsQuestion,
    }
  )

  const [createQuestionTemplate] = useMutation(AtsCreateQuestionTemplateDocument, {
    extractorFn: (data) => data.atsMutations.createAtsQuestionTemplate,
  })

  const templateForm = useForm<CreateQuestionFormValues>({
    defaultValues: {
      label: '',
      mandatory: false,
      autoDisqualify: false,
      questionType: 'long_text',
      options: [],
    },
    validate(data) {
      return validateQuestions(data, {})
    },
    async onSubmit(values, { setErrors }) {
      const shouldSendOptions = questionTypeHasOptions(values.questionType)

      const result = await createQuestionTemplate({
        variables: {
          companyId: companyId,
          options: shouldSendOptions ? values.options : null,
          autoDisqualify: values.autoDisqualify,
          label: values.label,
          mandatory: values.mandatory,
          questionType: values.questionType,
        },
      })

      const handled = handleResult(result, ({ label, mandatory, questionType }) => {
        setErrors({
          label,
          mandatory,
          questionType,
        })
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
      }

      return handled
    },
  })

  const submitTemplateForm = async () => {
    templateForm.setValues({
      label: item.label,
      mandatory: item.mandatory,
      autoDisqualify: item.autoDisqualify,
      questionType: item.questionType,
      options: item.options || [],
    })

    const result = await createQuestionTemplate({
      variables: {
        companyId: companyId,
        options: questionTypeHasOptions(item.questionType)
          ? item.options?.map((option) => ({
              text: option.text,
              disqualifies: option.disqualifies,
            }))
          : null,
        autoDisqualify: item.autoDisqualify,
        label: item.label,
        mandatory: item.mandatory,
        questionType: item.questionType,
      },
    })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.save_error'),
      })
    }

    return handled
  }

  const removeCustomQuestion = async () => {
    const result = await deleteCustomQuestion({
      variables: { id: item.id },
      update(cache) {
        cache.evict({ id: cache.identify(item) })
      },
    })

    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.removed_successfully'),
      })
    }

    return handled
  }

  return (
    <>
      <Box
        width="full"
        flexDirection="row"
        gap="s24"
        alignItems="center"
        justifyContent="spaceBetween"
        padding="s16"
      >
        <Box minWidth="s24">
          <Icon.Medium icon="drag-and-drop" />
        </Box>
        <Box gap="s8" flexGrow>
          <Text.Normal size="200" color="black" weight="medium">
            {item.label}
          </Text.Normal>
          {(item.mandatory || item.autoDisqualify) && (
            <Text.Normal size="200" color="grey800" weight="normal" capitalize>
              {item.mandatory && i18n.t('common.mandatory')}
              {item.autoDisqualify && item.mandatory && ' / '}
              {item.autoDisqualify &&
                i18n.t(
                  'ats.job_posting_settings.application_form.custom_questions.auto_disqualify'
                )}
            </Text.Normal>
          )}
        </Box>
        <Box minWidth="fit-content">
          <F0TagDot
            color={getQuestionTagColor(item.questionType ?? 'default')}
            text={getQuestionTypeLabel(item.questionType)}
          />
        </Box>
        <PopoverMenu type="list">
          <PopoverMenu.Trigger>
            <Button.Decorative
              type="secondary"
              aria-label={i18n.t('common.more_options')}
              iconName="ellipsis"
            />
          </PopoverMenu.Trigger>
          <PopoverMenu.Content side="bottom" align="end">
            <PopoverMenu.TouchableOption
              title={i18n.t('common.edit')}
              onClick={() => setEditQuestionOpen(true)}
            />
            {canSaveTemplates && (
              <PopoverMenu.TouchableOption
                title={i18n.t('ats.questions.save_as_template')}
                onClick={() => submitTemplateForm()}
              />
            )}
            <PopoverMenu.TouchableOption
              title={i18n.t('common.delete')}
              onClick={() => setDeleteQuestionOpen(true)}
              danger
            />
          </PopoverMenu.Content>
        </PopoverMenu>
      </Box>
      {deleteQuestionOpen && (
        <Modal
          illustration="delete"
          title={i18n.t(
            'ats.job_posting_settings.application_form.custom_questions.delete_modal_title'
          )}
          description={i18n.t(
            'ats.job_posting_settings.application_form.custom_questions.delete_modal_description'
          )}
          onClose={() => setDeleteQuestionOpen(false)}
          onSubmit={removeCustomQuestion}
          footer={
            <Box flexDirection="row" gap="s16">
              <Button type="secondary" onClick={() => setDeleteQuestionOpen(false)}>
                {i18n.t('common.cancel')}
              </Button>
              <Button type="negative" disabled={loadingDelete} submit>
                {i18n.t('common.delete')}
              </Button>
            </Box>
          }
        />
      )}
      {editQuestionOpen && (
        <EditCustomQuestion
          customQuestion={item}
          onClose={() => setEditQuestionOpen(false)}
          canSaveTemplates={canSaveTemplates}
          companyId={companyId}
        />
      )}
    </>
  )
}
